import { anyEntityWith, findAncestorEntity } from "../utils/bit-utils";
import { CONSTANTS } from "three-ammo";
const { DISABLE_DEACTIVATION, ACTIVE_TAG } = CONSTANTS.ACTIVATION_STATE;

import {
  addComponent,
  defineQuery,
  enterQuery,
  entityExists,
  removeComponent,
  exitQuery,
  hasComponent,
  Not
} from "bitecs";
import {
  RemoteRight,
  RemoteLeft,
  HandRight,
  HandLeft,
  HeldRemoteRight,
  HeldRemoteLeft,
  HeldHandRight,
  HeldHandLeft,
  OffersHandConstraint,
  OffersRemoteConstraint,
  Rigidbody,
  Constraint,
  ConstraintHandLeft,
  ConstraintHandRight,
  ConstraintRemoteLeft,
  ConstraintRemoteRight,
  GrabStartData,
  MovementThresholdActive,
  Held
} from "../bit-components";
import { takeOwnership } from "../utils/take-ownership";

const queryRemoteRight = defineQuery([HeldRemoteRight, OffersRemoteConstraint, MovementThresholdActive]);
const queryEnterRemoteRight = enterQuery(queryRemoteRight);
const queryExitRemoteRight = exitQuery(queryRemoteRight);

const queryRemoteLeft = defineQuery([HeldRemoteLeft, OffersRemoteConstraint, MovementThresholdActive]);
const queryEnterRemoteLeft = enterQuery(queryRemoteLeft);
const queryExitRemoteLeft = exitQuery(queryRemoteLeft);

const queryHandRight = defineQuery([HeldHandRight, OffersHandConstraint, MovementThresholdActive]);
const queryEnterHandRight = enterQuery(queryHandRight);
const queryExitHandRight = exitQuery(queryHandRight);

const queryHandLeft = defineQuery([HeldHandLeft, OffersHandConstraint, MovementThresholdActive]);
const queryEnterHandLeft = enterQuery(queryHandLeft);
const queryExitHandLeft = exitQuery(queryHandLeft);

const grabBodyOptions = { type: "dynamic", activationState: DISABLE_DEACTIVATION };
const releaseBodyOptions = { activationState: ACTIVE_TAG };

function add(world, physicsSystem, interactor, constraintComponent, entities) {
  for (let i = 0; i < entities.length; i++) {
    const eid = findAncestorEntity(world, entities[i], ancestor => hasComponent(world, Rigidbody, ancestor));
    takeOwnership(world, eid);
    physicsSystem.updateRigidBodyOptions(eid, grabBodyOptions);
    physicsSystem.addConstraint(interactor, Rigidbody.bodyId[eid], Rigidbody.bodyId[interactor]);
    addComponent(world, Constraint, eid);
    addComponent(world, constraintComponent, eid);
  }
}

function remove(world, offersConstraint, constraintComponent, physicsSystem, interactor, entities) {
  for (let i = 0; i < entities.length; i++) {
    const eid = findAncestorEntity(world, entities[i], ancestor => hasComponent(world, Rigidbody, ancestor));
    if (!entityExists(world, eid)) continue;
    if (hasComponent(world, offersConstraint, entities[i]) && hasComponent(world, Rigidbody, eid)) {
      physicsSystem.updateRigidBodyOptions(eid, releaseBodyOptions);
      physicsSystem.removeConstraint(interactor);
      removeComponent(world, constraintComponent, eid);
      removeComponent(world, MovementThresholdActive, eid);
      if (
        !hasComponent(world, ConstraintHandLeft, eid) &&
        !hasComponent(world, ConstraintHandRight, eid) &&
        !hasComponent(world, ConstraintRemoteLeft, eid) &&
        !hasComponent(world, ConstraintRemoteRight, eid)
      ) {
        removeComponent(world, Constraint, eid);
      }
    }
  }
}

export function constraintsSystem(world, physicsSystem) {
  add(world, physicsSystem, anyEntityWith(world, RemoteRight), ConstraintRemoteRight, queryEnterRemoteRight(world));
  add(world, physicsSystem, anyEntityWith(world, RemoteLeft), ConstraintRemoteLeft, queryEnterRemoteLeft(world));
  add(world, physicsSystem, anyEntityWith(world, HandRight), ConstraintHandRight, queryEnterHandRight(world));
  add(world, physicsSystem, anyEntityWith(world, HandLeft), ConstraintHandLeft, queryEnterHandLeft(world));
  remove(
    world,
    OffersRemoteConstraint,
    ConstraintRemoteRight,
    physicsSystem,
    anyEntityWith(world, RemoteRight),
    queryExitRemoteRight(world)
  );
  remove(
    world,
    OffersRemoteConstraint,
    ConstraintRemoteLeft,
    physicsSystem,
    anyEntityWith(world, RemoteLeft),
    queryExitRemoteLeft(world)
  );
  remove(
    world,
    OffersHandConstraint,
    ConstraintHandRight,
    physicsSystem,
    anyEntityWith(world, HandRight),
    queryExitHandRight(world)
  );
  remove(
    world,
    OffersHandConstraint,
    ConstraintHandLeft,
    physicsSystem,
    anyEntityWith(world, HandLeft),
    queryExitHandLeft(world)
  );
}

const MOVEMENT_THRESHOLD = 0.02; // Meters
const MOVEMENT_THRESHOLD_SQ = MOVEMENT_THRESHOLD * MOVEMENT_THRESHOLD; // Use squared distance

// This system is used to add a resistance threshold before moving a held object
const thresholdCheckQuery = defineQuery([Held, GrabStartData, Not(MovementThresholdActive)]);
export function thresholdSystem(world) {
  const thresholdCheckEntities = thresholdCheckQuery(world);

  for (let i = 0; i < thresholdCheckEntities.length; i++) {
    const eid = thresholdCheckEntities[i];
    let interactorComponent;

    if (hasComponent(world, HeldRemoteRight, eid)) interactorComponent = RemoteRight;
    if (hasComponent(world, HeldRemoteLeft, eid)) interactorComponent = RemoteLeft;
    if (hasComponent(world, HeldHandRight, eid)) interactorComponent = HandRight;
    if (hasComponent(world, HeldHandLeft, eid)) interactorComponent = HandLeft;

    const interactorEid = anyEntityWith(world, interactorComponent);
    const interactor = world.eid2obj?.get(interactorEid); // Use optional chaining
    if (!interactor) continue; // Object might not be ready

    const dx = interactor.position.x - GrabStartData.interactorX[eid];
    const dy = interactor.position.y - GrabStartData.interactorY[eid];
    const dz = interactor.position.z - GrabStartData.interactorZ[eid];
    const distanceSq = dx * dx + dy * dy + dz * dz;

    if (distanceSq >= MOVEMENT_THRESHOLD_SQ) {
      removeComponent(world, GrabStartData, eid);
      addComponent(world, MovementThresholdActive, eid);
    }
  }
}
